/**
 * Create Account Page — CHI Alliance
 * Obsidian Pulse design system: dark #080C18, magenta/cyan gradients, Bebas Neue headings, Space Grotesk body.
 * Multi-step registration: Identity, Professional, Membership, then Community.
 * Mobile-first, all fields validated, profile photo upload preview.
 */
import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Link, useLocation } from "wouter";
import {
  User, Mail, Phone, Building2, Briefcase, Linkedin, FileText,
  Camera, ChevronRight, ChevronLeft, Check, ArrowRight, Sparkles,
  X, Upload, BriefcaseBusiness, Crown, KeyRound, ShieldCheck
} from "lucide-react";
import BrandLogoMark from "@/components/BrandLogoMark";
import { Field, FilterableSelectInput, SelectInput, TextInput, TextareaInput, inputStyle } from "@/components/memberForm/fields";
import DirectoryPrivacyControls from "@/components/DirectoryPrivacyControls";
import InterestTypeahead from "@/components/InterestTypeahead";
import { MEMBER_INTERESTS } from "@shared/memberInterests";
import { MAX_IMAGE_UPLOAD_BYTES, MAX_IMAGE_UPLOAD_LABEL } from "@shared/imageUploadLimits";
import { trpc } from "@/lib/trpc";
import { toSafeAppPath } from "@shared/authRouting";
import {
  NEW_ACCOUNT_DIRECTORY_PRIVACY,
  type DirectoryPrivacyPreferences,
} from "@shared/directoryPrivacy";
import {
  LINKEDIN_PROFILE_PREFIX,
  normalizeInternationalPhone,
  normalizeLinkedInProfile,
} from "@shared/profileInputs";
import SocialLinksInput, { CompanyWebsiteInput, socialLinkDraftError, type SocialLinkDraft } from "@/components/SocialLinksInput";
import { PhoneInput, splitStoredPhone } from "@/components/ProfileContactInputs";
import PhotoPicker from "@/components/PhotoPicker";
import { normalizeCompanyWebsite } from "@shared/socialLinks";
import { handoffToCheckout } from "@/lib/checkoutWindow";
import { resolveSignupCompletion, type SignupAccountType } from "@/lib/signupFlow";
import { INDUSTRIES, OTHER_INDUSTRY } from "@shared/industries";
import { MEMBERSHIP_PAID_TIERS_ENABLED } from "@shared/membershipPaidTiers";

// ── AREAS OF INTEREST ────────────────────────────────────────────────────────

// ── STEP DEFINITIONS ─────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, label: "Identity", icon: User },
  { id: 2, label: "Professional", icon: Briefcase },
  { id: 3, label: "Community", icon: Sparkles },
  { id: 4, label: "Membership", icon: Crown },
];

const MEMBERSHIP_BENEFITS = [
  { label: "Register for events", free: true },
  { label: "Browse speakers and member directory", free: true },
  { label: "Complete professional profile", free: false },
  { label: "Verified profile links and full bio", free: false },
  { label: "Priority event registration", free: false },
  { label: "Member badge", free: false },
];

// ── FORM STATE ───────────────────────────────────────────────────────────────
interface FormData {
  fullName: string;
  email: string;
  phone: string;
  phoneCountryCode: string;
  company: string;
  title: string;
  industry: string;
  industryOther: string;
  linkedinUrl: string;
  socialLinks: SocialLinkDraft[];
  companyWebsiteUrl: string;
  bio: string;
  interests: string[];
  photoDataUrl: string | null;
  directoryPrivacy: DirectoryPrivacyPreferences;
  openToWork: boolean;
  resumeFile: File | null;
  resumeName: string;
  accountType: SignupAccountType | "";
}

const INITIAL: FormData = {
  fullName: "",
  email: "",
  phone: "",
  phoneCountryCode: "+1",
  company: "",
  title: "",
  industry: "",
  industryOther: "",
  linkedinUrl: "",
  socialLinks: [],
  companyWebsiteUrl: "",
  bio: "",
  interests: [],
  photoDataUrl: null,
  directoryPrivacy: { ...NEW_ACCOUNT_DIRECTORY_PRIVACY },
  openToWork: false,
  resumeFile: null,
  resumeName: "",
  accountType: MEMBERSHIP_PAID_TIERS_ENABLED ? "" : "free",
};

// ── FIELD COMPONENT ───────────────────────────────────────────────────────────
function StepIndicator({ current }: { current: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0", marginBottom: "2rem" }}>
      {STEPS.map((step, i) => {
        const done = current > step.id;
        const active = current === step.id;
        return (
          <div key={step.id} style={{ display: "flex", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.35rem" }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: done ? "linear-gradient(135deg, #E91E8C, #C2185B)" : active ? "rgba(233,30,140,0.15)" : "rgba(255,255,255,0.04)",
                border: `2px solid ${done ? "transparent" : active ? "rgba(233,30,140,0.6)" : "rgba(255,255,255,0.1)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.3s ease",
              }}>
                {done ? (
                  <Check size={16} style={{ color: "white" }} />
                ) : (
                  <step.icon size={15} style={{ color: active ? "#E91E8C" : "#475569" }} />
                )}
              </div>
              <span style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "0.65rem",
                fontWeight: active ? 700 : 400,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: active ? "#E91E8C" : done ? "#94A3B8" : "#475569",
              }}>{step.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{
                width: "clamp(2rem, 8vw, 4rem)", height: 1,
                background: done ? "linear-gradient(90deg, #E91E8C, rgba(233,30,140,0.3))" : "rgba(255,255,255,0.08)",
                margin: "0 0.5rem", marginBottom: "1.25rem",
                transition: "background 0.3s ease",
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Mirrors the types server/memberPhoto.ts will accept, so a refusal happens here. */
const ACCEPTED_PHOTO_TYPES = ["image/png", "image/jpeg", "image/webp"];

// ── PHOTO UPLOAD ──────────────────────────────────────────────────────────────

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function CreateAccount() {
  const [, navigate] = useLocation();
  const signupRouting = useMemo(() => {
    if (typeof window === "undefined") return { isTicketCheckoutSource: false, returnTo: undefined };
    const params = new URLSearchParams(window.location.search);
    const isTicketCheckoutSource = params.get("source") === "ticket-checkout";
    const safeReturnTo = toSafeAppPath(params.get("returnTo"));
    return {
      isTicketCheckoutSource,
      returnTo: isTicketCheckoutSource ? (safeReturnTo === "/guest-tickets" ? safeReturnTo : "/guest-tickets") : safeReturnTo,
    };
  }, []);
  const { isTicketCheckoutSource, returnTo } = signupRouting;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(INITIAL);
  const touchedAccountFields = useRef(new Set<keyof FormData>());
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [accountExistsAfterSubmit, setAccountExistsAfterSubmit] = useState(false);
  const signupAttemptId = useRef(globalThis.crypto?.randomUUID?.()).current;
  const submissionAlertRef = useRef<HTMLDivElement>(null);
  const signupFormRef = useRef<HTMLFormElement>(null);
  const [createdEmail, setCreatedEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("");
  const [deliveryFailed, setDeliveryFailed] = useState(false);
  /**
   * True when the server filled the code in for us, which it does only under the
   * local development gate in server/localOtpPrefill.ts. Tracked separately from
   * the code itself so the banner keeps showing after the field is edited, and so
   * a prefilled code can unblock the submit button when there is no mail provider
   * to have delivered anything.
   */
  const [codePrefilledLocally, setCodePrefilledLocally] = useState(false);
  /** Address confirmed to already have an account, so the notice can name it. */
  const [emailTaken, setEmailTaken] = useState("");
  const checkEmailAvailability = trpc.auth.checkEmailAvailability.useMutation();
  const registerAccount = trpc.auth.registerAccount.useMutation();
  const requestOtp = trpc.auth.requestOtp.useMutation();
  const verifyOtp = trpc.auth.verifyOtp.useMutation();
  const startCheckout = trpc.members.startAnnualCheckout.useMutation();
  const uploadPhoto = trpc.members.uploadMyPhoto.useMutation();
  const ticketGuestSessionQuery = trpc.events.ticketGuestSession.useQuery(undefined, {
    enabled: isTicketCheckoutSource,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [photoError, setPhotoError] = useState("");
  const utils = trpc.useUtils();
  const isEventReturn = Boolean(returnTo?.startsWith("/events/"));

  const showSubmissionError = useCallback((message: string) => {
    setSubmissionError(message);
    requestAnimationFrame(() => {
      submissionAlertRef.current?.focus();
    });
  }, []);

  const showValidationError = (blockedStep: number) => {
    /*
     * Step 4 already has one specific assertive error below the radio group.
     * Rendering the generic summary too would announce two alerts for one action.
     */
    setSubmissionError(
      blockedStep === 4
        ? ""
        : `Please complete the highlighted fields before continuing. Support code: ${signupAttemptId?.slice(0, 8) ?? "unavailable"}.`,
    );
    const mountedTarget = blockedStep === 4
      ? signupFormRef.current?.querySelector<HTMLElement>("[role='radio']")
      : signupFormRef.current?.querySelector<HTMLElement>("input:invalid, select:invalid");
    if (mountedTarget) {
      mountedTarget.focus();
      return;
    }
    requestAnimationFrame(() => {
      const target = blockedStep === 4
        ? signupFormRef.current?.querySelector<HTMLElement>("[role='radio']")
        : signupFormRef.current?.querySelector<HTMLElement>("[aria-invalid='true'], input:invalid, select:invalid");
      (target ?? submissionAlertRef.current)?.focus();
    });
  };

  const set = (key: keyof FormData) => (value: string) => {
    touchedAccountFields.current.add(key);
    setForm((f) => ({ ...f, [key]: value }));
  };

  /** Editing the address invalidates a notice that named the previous one. */
  const setEmailField = (value: string) => {
    set("email")(value);
    if (emailTaken) setEmailTaken("");
  };

  useEffect(() => {
    if (!isTicketCheckoutSource) return;
    const guest = ticketGuestSessionQuery.data as {
      email?: unknown; name?: unknown; companyName?: unknown; jobTitle?: unknown; phone?: unknown;
    } | null | undefined;
    if (!guest || typeof guest.email !== "string") return;
    const phone = splitStoredPhone(typeof guest.phone === "string" ? guest.phone : "");
    setForm(current => {
      const next = { ...current };
      const fill = (key: "fullName" | "email" | "company" | "title", candidate: unknown) => {
        if (typeof candidate === "string" && candidate.trim() && !touchedAccountFields.current.has(key) && !current[key].trim()) {
          next[key] = candidate.trim();
        }
      };
      fill("fullName", guest.name);
      fill("email", guest.email);
      fill("company", guest.companyName);
      fill("title", guest.jobTitle);
      if (!touchedAccountFields.current.has("phone") && !current.phone.trim() && phone.national) {
        next.phoneCountryCode = phone.countryCode;
        next.phone = phone.national;
      }
      return next;
    });
  }, [isTicketCheckoutSource, ticketGuestSessionQuery.data]);

  /**
   * Checks for an existing account once the field is committed, not per
   * keystroke: keystroke checks fire a request per character, leak a partial
   * address on every one, and burn the caller's throttle allowance before the
   * member has finished typing.
   *
   * Silent on anything other than a definite "taken" — a throttled or failed
   * check must not imply the address is free. Submitting still rejects a
   * duplicate, so nothing depends on this succeeding.
   */
  const handleEmailBlur = async () => {
    const candidate = form.email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate)) return;

    try {
      const result = await checkEmailAvailability.mutateAsync({ email: candidate });
      setEmailTaken(result.status === "taken" ? candidate : "");
    } catch {
      setEmailTaken("");
    }
  };

  const toggleInterest = (interest: string) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(interest)
        ? f.interests.filter((i) => i !== interest)
        : [...f.interests, interest],
    }));
  };

  // ── VALIDATION ──────────────────────────────────────────────────────────────
  const validateStep = (s: number): boolean => {
    const errs: Partial<Record<keyof FormData, string>> = {};
    if (s === 1) {
      if (!form.fullName.trim()) errs.fullName = "Full name is required";
      if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Valid email required";
    }
    if (s === 2) {
      if (!form.company.trim()) errs.company = "Company / Organization is required";
      if (!form.title.trim()) errs.title = "Job title is required";
      if (!form.industry) errs.industry = "Please select your industry";
      else if (form.industry === OTHER_INDUSTRY && !form.industryOther.trim()) {
        errs.industryOther = "Please enter your industry";
      }
      if (form.linkedinUrl.trim()) {
        try {
          normalizeLinkedInProfile(form.linkedinUrl);
        } catch (error) {
          errs.linkedinUrl = error instanceof Error ? error.message : "Enter a LinkedIn profile username or /in/ URL.";
        }
      }
      // Optional as a whole, but a row that was filled in has to be usable.
      const socialError = socialLinkDraftError(form.socialLinks);
      if (socialError) errs.socialLinks = socialError;
      if (form.companyWebsiteUrl.trim()) {
        try {
          normalizeCompanyWebsite(form.companyWebsiteUrl);
        } catch (error) {
          errs.companyWebsiteUrl = error instanceof Error ? error.message : "Enter a complete web address.";
        }
      }
    }
    if (s === 3) {
      if (form.interests.length === 0) errs.interests = "Select at least one area of interest";
    }
    if (s === 4 && MEMBERSHIP_PAID_TIERS_ENABLED) {
      if (!form.accountType) errs.accountType = "Choose Free Account or Paid Member to continue";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (validateStep(step)) {
      setSubmissionError("");
      setStep((s) => Math.min(s + 1, 4));
      return;
    }
    showValidationError(step);
  };

  const back = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 1));
  };

  /**
   * What actually gets stored as the industry.
   *
   * With "Other" chosen, the typed value replaces the sentinel — storing the word
   * "Other" would leave the directory with a category that describes nobody.
   */
  const resolvedIndustry = form.industry === OTHER_INDUSTRY
    ? form.industryOther.trim()
    : form.industry;

  /**
   * Fills the passcode field from a code the server returned.
   *
   * The server only ever returns one when running against a local development
   * database with DEV_PREFILL_OTP=1, so in every other environment this is called
   * with undefined and does nothing.
   */
  const applyLocalPrefillCode = (code: string | undefined) => {
    if (!code) return;
    setVerificationCode(code);
    setCodePrefilledLocally(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    for (let candidateStep = 1; candidateStep <= 4; candidateStep += 1) {
      if (!validateStep(candidateStep)) {
        setStep(candidateStep);
        showValidationError(candidateStep);
        return;
      }
    }
    if (!form.accountType) return;
    setSubmissionError("");
    setAccountExistsAfterSubmit(false);
    try {
      const phone = normalizeInternationalPhone(form.phoneCountryCode, form.phone);
      const result = await registerAccount.mutateAsync({
        signupAttemptId,
        fullName: form.fullName,
        email: form.email,
        phone,
        company: form.company,
        jobTitle: form.title,
        industry: resolvedIndustry,
        linkedinUrl: normalizeLinkedInProfile(form.linkedinUrl),
        // Sent as typed; the server normalizes with the same shared rules the
        // form validated against, so it stays the single authority on storage.
        socialLinks: form.socialLinks.map(link => ({ platform: link.platform, url: link.value })),
        companyWebsiteUrl: form.companyWebsiteUrl.trim() || undefined,
        bio: form.bio || undefined,
        interests: form.interests,
        directoryPrivacy: form.directoryPrivacy,
        openToWork: form.openToWork,
        accountType: form.accountType,
      });
      setCreatedEmail(result.email);
      setDeliveryFailed(result.delivery === "failed");
      applyLocalPrefillCode(result.localPrefillCode);
      setVerificationStatus(
        result.delivery === "sent"
          ? `A six-digit code was sent to ${result.email}.`
          : result.localPrefillCode
            ? "Your account was created. Email delivery is not configured here, so the code below was filled in for you."
            : `Your account was created, but the code could not be delivered. Send a new code to try again. Support code: ${result.supportCode}.`,
      );
      setSubmitted(true);
    } catch (submissionFailure) {
      const message = submissionFailure instanceof Error ? submissionFailure.message : "";
      if (/account already exists/i.test(message)) {
        setAccountExistsAfterSubmit(true);
        showSubmissionError(message);
      } else if (/too many account-creation attempts/i.test(message) || /support code/i.test(message)) {
        showSubmissionError(message);
      } else {
        showSubmissionError(`We could not create your account. Please try again. If the problem continues, share support code ${signupAttemptId?.slice(0, 8) ?? "unavailable"}.`);
      }
    }
  };

  const handleLinkedInChange = (value: string) => {
    if (!value.includes("linkedin.com/")) {
      setForm(formValue => ({ ...formValue, linkedinUrl: value.replace(/^@/, "") }));
      return;
    }
    try {
      const normalized = normalizeLinkedInProfile(value);
      setForm(formValue => ({
        ...formValue,
        linkedinUrl: normalized?.slice(LINKEDIN_PROFILE_PREFIX.length) ?? "",
      }));
    } catch {
      setForm(formValue => ({ ...formValue, linkedinUrl: value }));
    }
  };

  const resendVerificationCode = async () => {
    setVerificationError("");
    try {
      const result = await requestOtp.mutateAsync({ email: createdEmail, signupAttemptId });
      const prefill = "localPrefillCode" in result ? result.localPrefillCode : undefined;
      applyLocalPrefillCode(prefill);
      const resendFailed = "delivery" in result && result.delivery === "failed";
      setDeliveryFailed(resendFailed);
      setVerificationStatus(
        prefill
          ? "A new code was issued and filled in below."
          : resendFailed
            ? `A new code could not be delivered. Try again shortly. Support code: ${"supportCode" in result ? result.supportCode : signupAttemptId?.slice(0, 8) ?? "unavailable"}.`
          : `If the account is active, a new six-digit code is on its way to ${createdEmail}.`,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      setVerificationError(
        /support code/i.test(message)
          ? message
          : `A new code could not be sent. Try again. If the problem continues, share support code ${signupAttemptId?.slice(0, 8) ?? "unavailable"}.`,
      );
    }
  };

  const handleVerify = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(verificationCode)) {
      setVerificationError("Enter the six-digit code from your email.");
      return;
    }

    setVerificationError("");
    try {
      await verifyOtp.mutateAsync({ signupAttemptId, email: createdEmail, code: verificationCode, returnTo });
      await utils.auth.me.invalidate();

      /*
       * The photo is stored here, not at step 1, because uploading it requires a
       * session and the account did not exist yet when it was chosen.
       *
       * A failure is reported and swallowed. The account is created and verified by
       * this point, so failing the whole flow — and stranding a paid signup before
       * checkout — because an image did not store would be the worse outcome. The
       * member can add it again from My Profile.
       */
      if (form.photoDataUrl) {
        try {
          await uploadPhoto.mutateAsync({ dataUrl: form.photoDataUrl });
        } catch {
          setPhotoError("Your account is ready, but the photo could not be saved. You can add it from My Profile.");
        }
      }

      const completion = resolveSignupCompletion(form.accountType || "free", returnTo);
      if (completion.kind === "navigate") {
        navigate(completion.destination);
        return;
      }
      /*
       * Return to the member's own events, not the membership sales page.
       *
       * The default success path is /membership, which is right for an upgrade
       * started there and wrong here: a member who has just paid during signup
       * landing back on the page selling the thing they bought reads as though
       * the purchase did not register.
       *
       * A relative path — the server sanitises it and refuses anything that is
       * not same-origin, since it becomes a Stripe redirect target.
       */
      const checkout = await startCheckout.mutateAsync({
        successPath: returnTo ?? "/my-events",
      });
      handoffToCheckout(checkout.checkoutUrl, null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      setVerificationError(
        /invalid, expired, or has already been used/i.test(message) || /support code/i.test(message)
          ? message
          : `The code could not be verified. Request a new code and try again. If the problem continues, share support code ${signupAttemptId?.slice(0, 8) ?? "unavailable"}.`,
      );
    }
  };

  // ── SUCCESS STATE ────────────────────────────────────────────────────────────
  /*
   * Undelivered normally means there is nothing to type, so the submit is held
   * closed until a resend succeeds. A locally prefilled code is the exception:
   * delivery genuinely failed — there is no mail provider — but the code in the
   * field is real and will verify.
   */
  const blockVerification = deliveryFailed && !codePrefilledLocally;

  if (submitted) {
    return (
      <div style={{ minHeight: "100vh", background: "#080C18", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem 1rem" }}>
        <div style={{ textAlign: "center", width: "min(100%, 480px)", padding: "clamp(1.5rem,5vw,2.5rem)", borderRadius: "1.25rem", background: "rgba(13,18,32,.94)", border: "1px solid rgba(255,255,255,.09)", boxShadow: "0 28px 80px rgba(0,0,0,.55)" }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%", margin: "0 auto 1.25rem",
            background: deliveryFailed ? "rgba(245,166,35,.12)" : "linear-gradient(135deg, #E91E8C, #C2185B)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 40px rgba(233,30,140,0.4)",
          }}>
            <KeyRound size={28} style={{ color: deliveryFailed ? "#F5A623" : "white" }} />
          </div>
          <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 6vw, 3rem)", color: "white", marginBottom: "0.75rem" }}>
            Verify your email
          </h1>
          <p role="status" aria-live="polite" style={{ color: deliveryFailed ? "#FDE68A" : "#BAE6FD", fontFamily: "'Inter', sans-serif", fontSize: ".88rem", lineHeight: 1.65, margin: "0 0 1.35rem", padding: ".75rem", borderRadius: ".65rem", background: deliveryFailed ? "rgba(245,166,35,.08)" : "rgba(14,165,233,.08)", border: `1px solid ${deliveryFailed ? "rgba(245,166,35,.25)" : "rgba(14,165,233,.22)"}` }}>
            {verificationStatus}
          </p>
          <form onSubmit={handleVerify}>
            <label htmlFor="signup-verification-code" style={{ display: "block", textAlign: "left", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".72rem", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}>Six-digit code</label>
            <input id="signup-verification-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={verificationCode} onChange={event => setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter the 6-digit code" style={{ ...inputStyle, marginTop: ".45rem", minHeight: 54, textAlign: "center", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1.3rem", fontWeight: 700, letterSpacing: ".3em" }} />
            {/*
              Deliberately loud and unstyled-looking. A prefilled passcode is a
              development affordance, and the one thing worse than not having it is
              seeing it on a screen and not realising which environment you are in.
            */}
            {codePrefilledLocally && (
              <p role="status" style={{ margin: ".55rem 0 0", padding: ".5rem .65rem", borderRadius: ".5rem", background: "rgba(245,166,35,.1)", border: "1px dashed rgba(245,166,35,.45)", color: "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 650, textAlign: "left", lineHeight: 1.5 }}>
                Local development only — DEV_PREFILL_OTP is on, so this code came back from the server instead of an email.
              </p>
            )}
            <button type="submit" disabled={verifyOtp.isPending || startCheckout.isPending || blockVerification} style={{ width: "100%", minHeight: 48, marginTop: "1rem", border: 0, borderRadius: ".65rem", background: blockVerification ? "rgba(100,116,139,.35)" : "linear-gradient(135deg,#E91E8C,#C2185B)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: blockVerification ? "not-allowed" : "pointer" }}>
              {verifyOtp.isPending ? "Verifying…" : startCheckout.isPending ? "Opening checkout…" : form.accountType === "paid" ? "Verify & Continue to Checkout" : isEventReturn ? "Verify & Return to Event" : "Verify & Continue"}
            </button>
          </form>
          <button type="button" disabled={requestOtp.isPending} onClick={() => void resendVerificationCode()} style={{ width: "100%", minHeight: 42, marginTop: ".7rem", border: "1px solid rgba(255,255,255,.13)", borderRadius: ".6rem", background: "rgba(255,255,255,.025)", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 650, cursor: requestOtp.isPending ? "wait" : "pointer" }}>
            {requestOtp.isPending ? "Sending a new code…" : "Send a new code"}
          </button>
          {verificationError && <p role="alert" style={{ color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5, margin: ".75rem 0 0", textAlign: "left" }}>{verificationError}</p>}
          {/* Amber, not red: the account exists and only the photo did not store. */}
          {photoError && <p role="status" style={{ color: "#FCD34D", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5, margin: ".75rem 0 0", textAlign: "left" }}>{photoError}</p>}
        </div>
      </div>
    );
  }

  // ── CARD WRAPPER ─────────────────────────────────────────────────────────────
  const cardStyle: React.CSSProperties = {
    background: "rgba(8,12,24,0.85)",
    backdropFilter: "blur(28px)",
    WebkitBackdropFilter: "blur(28px)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: "1.5rem",
    padding: "clamp(1.5rem, 5vw, 2.5rem)",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(233,30,140,0.06)",
    width: "100%",
    maxWidth: 560,
    margin: "0 auto",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#080C18", position: "relative", overflow: "hidden" }}>
      {/* Background glow */}
      <div style={{ position: "fixed", inset: 0, background: "radial-gradient(ellipse at 30% 0%, rgba(233,30,140,0.08) 0%, transparent 60%), radial-gradient(ellipse at 70% 100%, rgba(0,212,255,0.06) 0%, transparent 60%)", pointerEvents: "none" }} />

      {/* Top bar */}
      <header style={{ position: "relative", zIndex: 10, padding: "1.25rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <Link href="/" style={{ display: "flex", alignItems: "center", gap: "0.625rem", textDecoration: "none" }}>
          <BrandLogoMark size="sm" />
          <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748B" }}>
            The CHI Alliance
          </span>
        </Link>
        <Link href="/login" style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.78rem", textDecoration: "none" }}>
          Already a member? <span style={{ color: "#E91E8C", fontWeight: 600 }}>Sign In</span>
        </Link>
      </header>

      {/* Main content */}
      <main style={{ position: "relative", zIndex: 1, padding: "2rem 1rem 4rem" }}>
        <div style={cardStyle}>
          {/* Top gradient bar */}
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg, #E91E8C, #F5A623, #00D4FF)" }} />

          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "1.75rem" }}>
            <div style={{ marginBottom: "0.75rem" }}>
              <BrandLogoMark size="sm" withGlow />
            </div>
            <h1 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(1.75rem, 5vw, 2.5rem)",
              fontWeight: 800, lineHeight: 1.1,
              color: "white", margin: "0 0 0.4rem",
            }}>
              Join the{" "}
              <span style={{ background: "linear-gradient(135deg, #E91E8C, #F5A623)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Alliance
              </span>
            </h1>
            <p style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", margin: 0 }}>
              Create your account in 4 focused steps
            </p>
          </div>

          {/* Step indicator */}
          <StepIndicator current={step} />

          {/* Step heading */}
          <div style={{ marginBottom: "1.5rem" }}>
            <div style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "0.65rem", fontWeight: 700,
              letterSpacing: "0.15em", textTransform: "uppercase",
              color: "#E91E8C", marginBottom: "0.25rem",
            }}>
              Step {step} of 4
            </div>
            <h2 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "1.5rem", fontWeight: 800,
              color: "white", margin: 0, letterSpacing: "0.02em",
            }}>
              {step === 1 && "Your Identity"}
              {step === 2 && "Professional Background"}
              {step === 3 && "Community Interests"}
              {step === 4 && (MEMBERSHIP_PAID_TIERS_ENABLED ? "Choose Your Membership" : "Join free")}
            </h2>
          </div>

          <form ref={signupFormRef} onSubmit={handleSubmit} noValidate>
            {/* ── STEP 1: IDENTITY ── */}
            {step === 1 && (
              <div>
                {/* Photo upload at top of step 1 */}
                <Field label="Profile Photo" hint="optional">
                  {/*
                    The shared picker, so signup frames a photo the same way every
                    other surface does. It hands back an already-cropped data URL;
                    the upload itself still happens after verification through
                    members.uploadMyPhoto, so the photo is normalized by the server
                    and stored through the managed application storage pipeline
                    exactly as before.
                  */}
                  <PhotoPicker
                    value={form.photoDataUrl}
                    onChange={dataUrl => { setPhotoError(""); setForm(f => ({ ...f, photoDataUrl: dataUrl })); }}
                    shape="circle"
                    label="profile photo"
                    size={104}
                  />
                  {photoError && (
                    <p role="alert" style={{ color: "#F87171", fontSize: "0.75rem", marginTop: "0.35rem", fontFamily: "'Inter', sans-serif" }}>
                      {photoError}
                    </p>
                  )}
                </Field>

                <Field label="Full Name" required>
                  <TextInput value={form.fullName} onChange={set("fullName")} placeholder="Enter your full name" icon={User} autoComplete="name" required invalid={Boolean(errors.fullName)} />
                  {errors.fullName && <p style={{ color: "#F87171", fontSize: "0.75rem", marginTop: "0.35rem", fontFamily: "'Inter', sans-serif" }}>{errors.fullName}</p>}
                </Field>

                <Field label="Email Address" required>
                  <TextInput value={form.email} onChange={setEmailField} onBlur={handleEmailBlur} placeholder="Enter your email address" type="email" icon={Mail} autoComplete="email" required invalid={Boolean(errors.email)} />
                  {errors.email && <p style={{ color: "#F87171", fontSize: "0.75rem", marginTop: "0.35rem", fontFamily: "'Inter', sans-serif" }}>{errors.email}</p>}

                  {/*
                    Already registered. Same copywriting rules as the sign-in
                    notice: plain language, name the address, and give the way
                    forward — which here is signing in, with the address carried
                    over so it is not typed twice.
                  */}
                  {emailTaken && (
                    <div role="status" aria-live="polite" style={{ margin: ".45rem 0 0", padding: ".7rem .8rem", borderRadius: ".55rem", color: "#FDE68A", background: "rgba(245,166,35,.08)", border: "1px solid rgba(245,166,35,.28)", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
                      <strong style={{ color: "#FFF" }}>{emailTaken}</strong> already has an account.{" "}
                      <Link href={`/login?email=${encodeURIComponent(emailTaken)}`} style={{ color: "#F9A8D4", fontWeight: 700, textDecoration: "none" }}>
                        Sign in instead
                      </Link>
                      .
                    </div>
                  )}
                </Field>

                <Field label="Phone Number" hint="optional">
                  {/*
                    The shared control, not a copy of it.
                    
                    This screen had its own country select and phone field —
                    the same idea rebuilt — so the flags, the separate United
                    States and Canada entries, and every future fix had to be
                    made twice. Signup and the admin forms now render the same
                    component.
                  */}
                  <PhoneInput
                    label="Phone number"
                    countryCode={form.phoneCountryCode}
                    value={form.phone}
                    onChange={(next: { countryCode: string; value: string }) => {
                      touchedAccountFields.current.add("phone");
                      setForm(value => ({ ...value, phoneCountryCode: next.countryCode, phone: next.value }));
                    }}
                  />
                  <p style={{ margin: ".35rem 0 0", color: "#475569", fontFamily: "'Inter', sans-serif", fontSize: ".7rem" }}>United States is selected by default. Canada and other countries are in the list.</p>
                </Field>

                <Field label="Bio" hint="optional">
                  <TextareaInput
                    value={form.bio}
                    onChange={(v) => setForm((f) => ({ ...f, bio: v.slice(0, 2500) }))}
                    placeholder="Tell the community about yourself — your background, what drives you, and what you're looking to build or explore…"
                    rows={6}
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.35rem" }}>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.7rem", color: form.bio.length > 2200 ? (form.bio.length >= 2500 ? "#F87171" : "#F5A623") : "#475569" }}>
                      {form.bio.length.toLocaleString()} / 2,500
                    </span>
                  </div>
                </Field>

                {/* ── OPEN TO WORK ── */}
                <div style={{ marginBottom: "1.25rem" }}>
                  {/* Toggle row */}
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, openToWork: !f.openToWork }))}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "1rem 1.125rem",
                      background: form.openToWork
                        ? "linear-gradient(135deg, rgba(34,197,94,0.08), rgba(16,185,129,0.05))"
                        : "rgba(255,255,255,0.03)",
                      border: form.openToWork
                        ? "1px solid rgba(34,197,94,0.35)"
                        : "1px solid rgba(255,255,255,0.08)",
                      borderRadius: "0.75rem",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: "0.5rem",
                        background: form.openToWork ? "rgba(34,197,94,0.15)" : "rgba(255,255,255,0.05)",
                        border: form.openToWork ? "1px solid rgba(34,197,94,0.3)" : "1px solid rgba(255,255,255,0.08)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.2s ease",
                        flexShrink: 0,
                      }}>
                        <BriefcaseBusiness size={16} style={{ color: form.openToWork ? "#22C55E" : "#475569" }} />
                      </div>
                      <div style={{ textAlign: "left" }}>
                        <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "0.875rem", color: form.openToWork ? "#22C55E" : "#94A3B8", margin: 0, transition: "color 0.2s" }}>
                          Open to Work
                        </p>
                        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: "#475569", margin: "0.15rem 0 0" }}>
                          Let members know you're open to new opportunities
                        </p>
                      </div>
                    </div>
                    {/* Custom toggle */}
                    <div style={{
                      width: 44, height: 24, borderRadius: 12,
                      background: form.openToWork ? "#22C55E" : "rgba(255,255,255,0.1)",
                      position: "relative", flexShrink: 0,
                      transition: "background 0.2s ease",
                    }}>
                      <div style={{
                        position: "absolute", top: 3,
                        left: form.openToWork ? 23 : 3,
                        width: 18, height: 18, borderRadius: "50%",
                        background: "white",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                        transition: "left 0.2s ease",
                      }} />
                    </div>
                  </button>

                  {/* Expanded panel */}
                  {form.openToWork && (
                    <div style={{
                      marginTop: "0.75rem",
                      padding: "1.25rem",
                      background: "rgba(34,197,94,0.04)",
                      border: "1px solid rgba(34,197,94,0.15)",
                      borderRadius: "0.75rem",
                    }}>
                      <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#22C55E", marginBottom: "1rem" }}>
                        Add Résumé Details
                      </p>

                      {/* Resume upload */}
                      <div>
                        <label style={{ display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748B", marginBottom: "0.4rem" }}>
                          Upload Résumé
                        </label>
                        <label
                          htmlFor="resume-upload"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            padding: "0.875rem 1rem",
                            background: form.resumeName ? "rgba(34,197,94,0.06)" : "rgba(255,255,255,0.03)",
                            border: form.resumeName ? "1px solid rgba(34,197,94,0.25)" : "1px dashed rgba(255,255,255,0.12)",
                            borderRadius: "0.625rem",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                        >
                          <div style={{ width: 34, height: 34, borderRadius: "0.4rem", background: form.resumeName ? "rgba(34,197,94,0.12)" : "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <FileText size={16} style={{ color: form.resumeName ? "#22C55E" : "#475569" }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {form.resumeName ? (
                              <>
                                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: "0.82rem", color: "#22C55E", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{form.resumeName}</p>
                                <p style={{ color: "#475569", fontFamily: "'Inter', sans-serif", fontSize: "0.7rem", margin: "0.1rem 0 0" }}>Tap to replace</p>
                              </>
                            ) : (
                              <>
                                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: "0.82rem", color: "#94A3B8", margin: 0 }}>Upload PDF or DOCX</p>
                                <p style={{ color: "#475569", fontFamily: "'Inter', sans-serif", fontSize: "0.7rem", margin: "0.1rem 0 0" }}>Max 5 MB · PDF, DOCX, DOC</p>
                              </>
                            )}
                          </div>
                          {form.resumeName && (
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); setForm((f) => ({ ...f, resumeFile: null, resumeName: "" })); }}
                              style={{ background: "none", border: "none", cursor: "pointer", padding: "0.25rem", color: "#475569", flexShrink: 0 }}
                            >
                              <X size={14} />
                            </button>
                          )}
                        </label>
                        <input
                          id="resume-upload"
                          type="file"
                          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setForm((f) => ({ ...f, resumeFile: file, resumeName: file.name }));
                          }}
                        />
                        <p style={{ color: "#334155", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5, marginTop: "0.5rem" }}>
                          Your résumé is only visible to premium members and is never shared outside the CHI Alliance network.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── STEP 2: PROFESSIONAL ── */}
            {step === 2 && (
              <div>
                <Field label="Company / Organization" required>
                  <TextInput value={form.company} onChange={set("company")} placeholder="Enter your company or organization" icon={Building2} autoComplete="organization" required invalid={Boolean(errors.company)} />
                  {errors.company && <p style={{ color: "#F87171", fontSize: "0.75rem", marginTop: "0.35rem", fontFamily: "'Inter', sans-serif" }}>{errors.company}</p>}
                </Field>

                <Field label="Job Title" required>
                  <TextInput value={form.title} onChange={set("title")} placeholder="Enter your job title" icon={Briefcase} autoComplete="organization-title" required invalid={Boolean(errors.title)} />
                  {errors.title && <p style={{ color: "#F87171", fontSize: "0.75rem", marginTop: "0.35rem", fontFamily: "'Inter', sans-serif" }}>{errors.title}</p>}
                </Field>

                <Field label="Industry" required>
                  <FilterableSelectInput
                    value={form.industry}
                    onChange={set("industry")}
                    options={INDUSTRIES}
                    placeholder="Select your industry"
                    searchPlaceholder="Type to filter (e.g. te)"
                    required
                    invalid={Boolean(errors.industry)}
                  />
                  {errors.industry && <p style={{ color: "#F87171", fontSize: "0.75rem", marginTop: "0.35rem", fontFamily: "'Inter', sans-serif" }}>{errors.industry}</p>}

                  {/* Revealed only by "Other", and required once shown — a blank
                      free-text field would store nothing more useful than the
                      word "Other" itself. */}
                  {form.industry === OTHER_INDUSTRY && (
                    <div style={{ marginTop: ".6rem" }}>
                      <TextInput
                        value={form.industryOther}
                        onChange={set("industryOther")}
                        placeholder="Tell us your industry"
                        icon={Briefcase}
                        required
                        invalid={Boolean(errors.industryOther)}
                      />
                      {errors.industryOther && <p style={{ color: "#F87171", fontSize: "0.75rem", marginTop: "0.35rem", fontFamily: "'Inter', sans-serif" }}>{errors.industryOther}</p>}
                    </div>
                  )}
                </Field>

                <Field label="LinkedIn username" hint="optional">
                  <div style={{ display: "flex", alignItems: "stretch", border: "1px solid rgba(255,255,255,.1)", borderRadius: ".625rem", overflow: "hidden", background: "rgba(255,255,255,.04)" }}>
                    <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", padding: "0 .72rem", borderRight: "1px solid rgba(255,255,255,.09)", color: "#7E8CA0", background: "rgba(255,255,255,.025)", fontFamily: "'Inter', sans-serif", fontSize: ".76rem", whiteSpace: "nowrap" }}><Linkedin size={14} /> {LINKEDIN_PROFILE_PREFIX}</span>
                    {/* No placeholder: the prefix already shows the expected shape,
                        and a sample username reads as a prefilled value. */}
                    <input aria-label="LinkedIn username" aria-invalid={Boolean(errors.linkedinUrl) || undefined} value={form.linkedinUrl} onChange={event => handleLinkedInChange(event.target.value)} autoComplete="url" style={{ ...inputStyle, border: 0, borderRadius: 0, background: "transparent", minWidth: 0 }} />
                  </div>
                  <p style={{ margin: ".35rem 0 0", color: "#475569", fontFamily: "'Inter', sans-serif", fontSize: ".7rem" }}>Type only your username, or paste your full LinkedIn profile URL.</p>
                  {errors.linkedinUrl && <p style={{ color: "#F87171", fontSize: "0.75rem", marginTop: "0.35rem", fontFamily: "'Inter', sans-serif" }}>{errors.linkedinUrl}</p>}
                </Field>

                <Field label="Company website">
                  <CompanyWebsiteInput
                    id="signup-company-website"
                    value={form.companyWebsiteUrl}
                    onChange={value => setForm(current => ({ ...current, companyWebsiteUrl: value }))}
                  />
                  {errors.companyWebsiteUrl && <p style={{ color: "#F87171", fontSize: "0.75rem", marginTop: "0.35rem", fontFamily: "'Inter', sans-serif" }}>{errors.companyWebsiteUrl}</p>}
                </Field>

                <Field label="Other social links">
                  {/* Same editor the admin member drawer uses, so a member's links
                      follow one set of rules wherever they are entered. */}
                  <SocialLinksInput
                    idPrefix="signup-social"
                    drafts={form.socialLinks}
                    onChange={next => setForm(current => ({ ...current, socialLinks: next }))}
                  />
                </Field>

              </div>
            )}

            {/* ── STEP 3: MEMBERSHIP ── */}
            {step === 4 && (
              <div>
                {MEMBERSHIP_PAID_TIERS_ENABLED ? (
                  <>
                <p style={{ margin: "0 0 1rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>
                  Choose how you want to participate. Your account is created only after the final step, and paid membership begins only after email verification and secure checkout.
                </p>
                <div role="radiogroup" aria-label="Membership choice" aria-required="true" aria-invalid={Boolean(errors.accountType) || undefined} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: ".75rem" }}>
                  {([
                    { value: "free" as const, label: "Free Account", price: "$0", accent: "#67E8F9", summary: "Join events and explore the community." },
                    { value: "paid" as const, label: "Paid Member", price: "$199 / year", accent: "#F472B6", summary: "Build a complete presence with priority access." },
                  ]).map(option => {
                    const selected = form.accountType === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setForm(value => ({ ...value, accountType: option.value }))}
                        style={{ position: "relative", minHeight: 178, padding: "1.15rem", textAlign: "left", borderRadius: ".9rem", border: `1px solid ${selected ? option.accent : "rgba(255,255,255,.11)"}`, background: selected ? `linear-gradient(145deg,${option.accent}18,rgba(13,18,32,.96) 55%)` : "rgba(255,255,255,.025)", color: "white", cursor: "pointer", boxShadow: selected ? `0 0 0 2px ${option.accent}22, 0 18px 45px rgba(0,0,0,.22)` : "none", transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease" }}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: ".35rem", minHeight: 24, color: option.accent, fontFamily: "'Space Grotesk', sans-serif", fontSize: ".68rem", fontWeight: 750, letterSpacing: ".09em", textTransform: "uppercase" }}>
                          {option.value === "paid" ? <Crown size={14} /> : <ShieldCheck size={14} />} {option.label}
                        </span>
                        <strong style={{ display: "block", marginTop: ".65rem", color: "#FFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.8rem", letterSpacing: ".025em" }}>{option.price}</strong>
                        <span style={{ display: "block", marginTop: ".35rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>{option.summary}</span>
                        <span style={{ position: "absolute", right: ".9rem", top: ".9rem", width: 21, height: 21, borderRadius: "50%", border: `1px solid ${selected ? option.accent : "rgba(255,255,255,.2)"}`, background: selected ? option.accent : "transparent", display: "grid", placeItems: "center" }}>{selected && <Check size={13} color="#080C18" strokeWidth={3} />}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.accountType && <p role="alert" style={{ color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: ".75rem", margin: ".55rem 0 0" }}>{errors.accountType}</p>}

                {/*
                  Says when payment happens, and only once paid is chosen.
                  Selecting "Paid Member" here charges nothing — checkout opens
                  after the final step and after the email code is verified, which
                  is deliberate: it avoids creating a Stripe customer for an
                  address nobody has proven they can receive mail at. Without this
                  note, picking a price and then being asked for more details reads
                  as though the payment step was skipped or lost.
                */}
                {form.accountType === "paid" && (
                  <div role="status" style={{ display: "flex", gap: ".55rem", alignItems: "flex-start", margin: ".7rem 0 0", padding: ".75rem .85rem", borderRadius: ".6rem", background: "rgba(0,212,255,.07)", border: "1px solid rgba(0,212,255,.24)" }}>
                    <ShieldCheck size={15} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1, color: "#67E8F9" }} />
                    <p style={{ margin: 0, color: "#CFFAFE", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
                      You won&rsquo;t be charged yet. Finish the next step and verify your email, then we&rsquo;ll take you to Stripe to complete the $199 annual payment securely.
                    </p>
                  </div>
                )}

                <section aria-labelledby="membership-benefits-heading" style={{ marginTop: "1.15rem", border: "1px solid rgba(103,232,249,.16)", borderRadius: ".9rem", overflow: "hidden", background: "var(--chi-table-surface)" }}>
                  <div style={{ padding: ".9rem 1rem", borderBottom: "1px solid rgba(255,255,255,.07)" }}>
                    <p style={{ margin: 0, color: "#67E8F9", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".65rem", fontWeight: 750, letterSpacing: ".11em", textTransform: "uppercase" }}>Quick review</p>
                    <h3 id="membership-benefits-heading" style={{ margin: ".2rem 0 0", color: "white", fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.25rem", letterSpacing: ".03em" }}>Compare membership benefits</h3>
                  </div>
                  <div role="table" aria-label="Free and paid membership benefits">
                    <div role="row" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 68px 68px", gap: ".35rem", padding: ".6rem .85rem", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".07em", textTransform: "uppercase" }}>
                      <span role="columnheader">Benefit</span><span role="columnheader" style={{ textAlign: "center" }}>Free</span><span role="columnheader" style={{ textAlign: "center", color: "#F472B6" }}>Paid</span>
                    </div>
                    {MEMBERSHIP_BENEFITS.map(benefit => (
                      <div key={benefit.label} role="row" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 68px 68px", alignItems: "center", gap: ".35rem", minHeight: 42, padding: ".5rem .85rem", borderTop: "1px solid rgba(255,255,255,.055)", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: ".73rem" }}>
                        <span role="cell">{benefit.label}</span>
                        <span role="cell" aria-label={benefit.free ? "Included with Free" : "Not included with Free"} style={{ display: "grid", placeItems: "center", color: benefit.free ? "#67E8F9" : "#475569" }}>{benefit.free ? <Check size={15} /> : "—"}</span>
                        <span role="cell" aria-label="Included with Paid" style={{ display: "grid", placeItems: "center", color: "#F472B6" }}><Check size={15} /></span>
                      </div>
                    ))}
                  </div>
                </section>
                  </>
                ) : (
                  <>
                    <p style={{ margin: "0 0 1rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>
                      Membership is free. After you verify your email, you will have full member access — no card and no checkout.
                    </p>
                    <section aria-labelledby="membership-benefits-heading" style={{ border: "1px solid rgba(103,232,249,.16)", borderRadius: ".9rem", overflow: "hidden", background: "var(--chi-table-surface)" }}>
                      <div style={{ padding: ".9rem 1rem", borderBottom: "1px solid rgba(255,255,255,.07)" }}>
                        <p style={{ margin: 0, color: "#67E8F9", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".65rem", fontWeight: 750, letterSpacing: ".11em", textTransform: "uppercase" }}>Included</p>
                        <h3 id="membership-benefits-heading" style={{ margin: ".2rem 0 0", color: "white", fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.25rem", letterSpacing: ".03em" }}>What you get with membership</h3>
                      </div>
                      <ul aria-label="Membership benefits" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                        {MEMBERSHIP_BENEFITS.map(benefit => (
                          <li key={benefit.label} style={{ display: "flex", alignItems: "center", gap: ".65rem", minHeight: 42, padding: ".5rem .85rem", borderTop: "1px solid rgba(255,255,255,.055)", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: ".73rem" }}>
                            <Check size={15} aria-hidden="true" style={{ color: "#67E8F9", flexShrink: 0 }} />
                            <span>{benefit.label}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  </>
                )}
              </div>
            )}

            {/* ── STEP 4: COMMUNITY ── */}
            {step === 3 && (
              <div>
                <Field label="Areas of Interest" required>
                  <p style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6, marginBottom: "0.875rem" }}>
                    Select the topics you'd like to learn about, speak on, or connect around. Choose as many as you like.
                  </p>
                  <InterestTypeahead
                    options={MEMBER_INTERESTS}
                    selected={form.interests}
                    onToggle={toggleInterest}
                    invalid={Boolean(errors.interests)}
                    required
                  />
                  {errors.interests && <p style={{ color: "#F87171", fontSize: "0.75rem", marginTop: "0.625rem", fontFamily: "'Inter', sans-serif" }}>{errors.interests}</p>}
                  {form.interests.length > 0 && (
                    <p style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", marginTop: "0.75rem" }}>
                      {form.interests.length} topic{form.interests.length !== 1 ? "s" : ""} selected
                    </p>
                  )}
                </Field>

                {/* Summary preview */}
                <div style={{
                  background: "rgba(233,30,140,0.05)",
                  border: "1px solid rgba(233,30,140,0.15)",
                  borderRadius: "0.875rem",
                  padding: "1.125rem 1.25rem",
                  marginTop: "1.5rem",
                }}>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#E91E8C", marginBottom: "0.875rem" }}>
                    Profile Summary
                  </h3>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.875rem", marginBottom: "0.875rem" }}>
                    {form.photoDataUrl ? (
                      <img src={form.photoDataUrl} alt="" style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(233,30,140,0.4)", flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(233,30,140,0.1)", border: "1px solid rgba(233,30,140,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <User size={20} style={{ color: "#E91E8C" }} />
                      </div>
                    )}
                    <div>
                      <p style={{ color: "white", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: "0.9rem", margin: 0 }}>{form.fullName || "—"}</p>
                      <p style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "0.78rem", margin: "0.15rem 0 0" }}>{form.title || "—"}{form.company ? ` · ${form.company}` : ""}</p>
                    </div>
                  </div>
                  {resolvedIndustry && (
                    <p style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "0.78rem", margin: "0 0 0.35rem" }}>
                      <span style={{ color: "#64748B" }}>Industry:</span> {resolvedIndustry}
                    </p>
                  )}
                  {form.email && (
                    <p style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "0.78rem", margin: 0 }}>
                      <span style={{ color: "#64748B" }}>Email:</span> {form.email}
                    </p>
                  )}
                </div>

                {/* Directory consent */}
                <div style={{ marginTop: "1.5rem" }}>
                  <DirectoryPrivacyControls
                    collapsibleFields
                    value={form.directoryPrivacy}
                    onChange={(directoryPrivacy) => setForm((current) => ({ ...current, directoryPrivacy }))}
                    preview={{
                      name: form.fullName,
                      title: form.title,
                      company: form.company,
                      industry: resolvedIndustry,
                      interests: form.interests,
                      bio: form.bio,
                      linkedinUrl: normalizeLinkedInProfile(form.linkedinUrl),
                      email: form.email,
                      phone: form.phone ? `${form.phoneCountryCode} ${form.phone}` : undefined,
                    }}
                  />
                </div>

                {/* Terms */}
                <p style={{ color: "#475569", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6, marginTop: "1.25rem", textAlign: "center" }}>
                  By creating an account you agree to our{" "}
                  <Link href="/privacy-policy" style={{ color: "#94A3B8", textDecoration: "underline" }}>Privacy Policy</Link>
                  {" "}and{" "}
                  <span style={{ color: "#94A3B8", textDecoration: "underline", cursor: "pointer" }}>Terms of Service</span>.
                </p>
              </div>
            )}

            {/* ── NAV BUTTONS ── */}
            <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.75rem" }}>
              {step > 1 && (
                <button
                  type="button"
                  onClick={back}
                  style={{
                    flex: "0 0 auto",
                    display: "flex", alignItems: "center", gap: "0.4rem",
                    padding: "0.85rem 1.25rem",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "0.625rem", color: "#94A3B8",
                    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600,
                    fontSize: "0.85rem", cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; e.currentTarget.style.color = "white"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "#94A3B8"; }}
                >
                  <ChevronLeft size={16} /> Back
                </button>
              )}
              {step < 4 ? (
                <button
                  type="button"
                  onClick={next}
                  style={{
                    flex: 1,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
                    padding: "0.9rem 1.5rem",
                    background: "linear-gradient(135deg, #E91E8C, #C2185B)",
                    border: "none", borderRadius: "0.625rem", color: "white",
                    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
                    fontSize: "0.9rem", letterSpacing: "0.04em",
                    cursor: "pointer", boxShadow: "0 4px 20px rgba(233,30,140,0.35)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(233,30,140,0.5)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(233,30,140,0.35)"; }}
                >
                  Continue <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={registerAccount.isPending}
                  style={{
                    flex: 1,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
                    padding: "0.9rem 1.5rem",
                    background: registerAccount.isPending ? "rgba(233,30,140,0.48)" : "linear-gradient(135deg, #E91E8C, #C2185B)",
                    border: "none", borderRadius: "0.625rem", color: "white",
                    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
                    fontSize: "0.9rem", letterSpacing: "0.04em",
                    cursor: registerAccount.isPending ? "wait" : "pointer", boxShadow: registerAccount.isPending ? "none" : "0 4px 20px rgba(233,30,140,0.35)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(233,30,140,0.5)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(233,30,140,0.35)"; }}
                >
                  {/*
                    Names what actually happens next. Membership is now the final
                    step, so a paid selection leads to email verification and then
                    payment — being told that beforehand is the difference between a
                    checkout the member chose and one they were dropped into.
                  */}
                  {registerAccount.isPending
                    ? "Creating Your Account…"
                    : form.accountType === "paid"
                      ? "Create Account & Continue to Payment"
                      : "Create My Account"} <ArrowRight size={16} />
                </button>
              )}
            </div>
            {submissionError && (
              <div ref={submissionAlertRef} tabIndex={-1} role="alert" aria-live="assertive" style={{ color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5, margin: "0.9rem 0 0", textAlign: "center", outline: "none" }}>
                <p style={{ margin: 0 }}>{submissionError}</p>
                {accountExistsAfterSubmit && (
                  <Link href={`/login?email=${encodeURIComponent(form.email.trim().toLowerCase())}`} style={{ display: "inline-block", marginTop: ".4rem", color: "#F9A8D4", fontWeight: 700 }}>
                    Sign in to the existing account
                  </Link>
                )}
              </div>
            )}
          </form>

          <p style={{ margin: "1.5rem 0 0", paddingTop: "1.25rem", borderTop: "1px solid rgba(255,255,255,0.07)", textAlign: "center", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "0.82rem" }}>
            Have an account?{" "}
            <Link href="/login" style={{ color: "#F9A8D4", fontWeight: 700, textDecoration: "none" }}>Login Here</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
